import { useState, useEffect, useRef } from 'react';
import { Row, Col, Container, Label, Spinner, Input, FormFeedback, UncontrolledTooltip, FormGroup } from 'reactstrap';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import PhoneInput from 'react-phone-number-input';
import ReCAPTCHA from "react-google-recaptcha";
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast, { Toaster } from 'react-hot-toast';
import { BsQuestionCircle } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

// Styles
import styles from './SignUp.module.css';

// Custom Component
import InputPasswordToggle from '../../components/input-password-toggle';

// Utility-Service
import { getToken, setUserSession } from '../../../utility/AuthService';

// API-Service
import { registerService } from '../../../services/authService';
import { getIndustry } from '../../../services/industryService';
import { demographicsListService } from '../../../services/demographicsService';
import { notificationQueueService } from '../../../services/notificationQueueService';
import { NotificationTypes } from '../../../services/notificationService';

//import custom list
// import states from '../../../utility/State';
// import cities from '../../../utility/City';

const RegisterPage = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [stepOneData, setStepOneData] = useState(null);
    const [stepTwoData, setStepTwoData] = useState(null);
    const recaptchaRef = useRef(null);
    const [isLoading, setLoading] = useState(false);
    const [captchaPass, setCaptchaPass] = useState(false);
    const [recaptchaResponse, setRecaptchaResponse] = useState();
    const [industryList, setIndustryList] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);

    const token = getToken();
    const navigate = useNavigate();

    const companySizeList = [
        {
            value: "Less than R300,000",
            label: "Less than R300,000"
        },
        {
            value: "R300,000 - 3 mil",
            label: "R300,000 - 3 mil"
        },
        {
            value: "R3 mil - 20 mil",
            label: "R3 mil - 20 mil"
        },
        {
            value: "R20 mil - 50 mil",
            label: "R20 mil - 50 mil"
        },
        {
            value: "R50 mil - 100 mil",
            label: "R50 mil - 100 mil"
        },
        {
            value: "R100 mil +",
            label: "R100 mil +"
        }
    ]

    // ** Hooks
    const { reset, watch, setValue, control, handleSubmit, clearErrors, setError, formState: { errors } } = useForm({
        mode: 'onChange'
    })
    const watchCountry = watch("country")
    const watchState = watch("state")

    /**
        * @function getCountryList
        * @param
        * @description get country list using api
    */
    const getCountryList = async () => {
        try {
            const result = await demographicsListService({
                type: 'Country',
                typeId: ""
            });
            if (result?.data?.statusCode === 200) {
                setCountryList(result?.data?.data?.list?.map(item => {
                    return {
                        value: item?._id,
                        label: item?.name
                    }
                }))
            }
        } catch (error) {
            setCountryList([])
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    };

    /**
        * @function getStateList
        * @param
        * @description get state list using api
    */
    const getStateList = async () => {
        try {
            const result = await demographicsListService({
                type: 'State',
                typeId: watchCountry?.value
            });
            if (result?.data?.statusCode === 200) {
                setStateList(result?.data?.data?.list?.map(item => {
                    return {
                        value: item?._id,
                        label: item?.name
                    }
                }))
            }
        } catch (error) {
            setStateList([])
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    };

    /**
        * @function getCityList
        * @param
        * @description get city list using api
    */
    const getCityList = async () => {
        try {
            const result = await demographicsListService({
                type: 'City',
                typeId: watchState?.value
            });
            if (result?.data?.statusCode === 200) {
                setCityList(result?.data?.data?.list?.map(item => {
                    return {
                        value: item?._id,
                        label: item?.name
                    }
                }))
            }
        } catch (error) {
            setCityList([])
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    };

    useEffect(() => {
        getCountryList()
    }, []);

    useEffect(() => {
        if (watchCountry?.value) {
            getStateList()
            setValue("state", null)
            setValue("city", null)
        }
    }, [watchCountry]);

    useEffect(() => {
        if (watchState?.value) {
            getCityList()
            setValue("city", null)
        }
    }, [watchState]);

    /**
        * @function getIndustryID
        * @param
        * @description get industry list using api
    */
    const getIndustryID = async () => {
        try {
            const result = await getIndustry({
                filter: 'Active',
                offset: 0
            });
            setIndustryList(result?.data?.data?.list.map(item => {
                return {
                    value: item?._id,
                    label: item?.name
                }
            }))
        } catch (error) {
            setIndustryList([])
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    };

    useEffect(() => {
        getIndustryID()
    }, []);

    useEffect(() => {
        if (token) {
            navigate('/user/history')
        }
    }, [])

    /**
        * @function signUP
        * @param input
        * @description handle signUp api to sign up user.
    */
    const signUP = async (input) => {
        try {
            setLoading(true)
            const result = await registerService(input)
            const { accessToken, refreshToken } = result?.data?.data?.tokens;

            if (result?.data?.statusCode === 200) {
                reset()
                setLoading(false)
                const getData = result?.data?.data

                if (getData?.tokens) {
                    setUserSession(accessToken, { ...getData?.user, language: 'English' }, refreshToken);
                    localStorage.setItem('accessToken', accessToken);
                    localStorage.setItem('refreshToken', refreshToken);
                    localStorage.setItem('userData', JSON.stringify({ ...getData?.user, language: 'English' }));

                    // Send welcome notification
                    await notificationQueueService.addToQueue({
                        type: NotificationTypes.ACCOUNT_CREATED,
                        userId: getData.user.id,
                        email: getData.user.email,
                        data: {
                            username: getData.user.username,
                            company: getData.user.company
                        },
                        priority: 'MEDIUM'
                    });

                    toast.success('Sign Up completed successfully')
                    // Redirect to history with first-time user journey
                    navigate('/user/history?journey=first_time');
                }
            }
        } catch (error) {
            recaptchaRef.current.reset()
            toast.error(error?.response?.data?.message || 'Something went wrong')
            setLoading(false)
        }
    }

    /**
        * @function handleRegister
        * @param data
        * @description prepare input data to call signUp api.
    */
    const handleRegister = async (data) => {
        const input = {
            username: data?.username,
            email: data?.email,
            company: data?.company,
            companyReg: data?.companyRegNum,
            companyLocation: data?.companyAddress,
            designation: data?.designation,
            mobile: data?.mobile,
            password: data?.password,
            industry: data?.industry?.value,
            country: data?.country?.label,
            city: data?.city?.label ? data?.city?.label : "",
            state: data?.state?.label,
            companySize: data?.companySize?.value,
            accessCode: data?.accessCode,
            repatcheResponse: recaptchaResponse
        }
        signUP(input)
    }

    /**
        * @function onSubmit
        * @param data
        * @description to submit
    */
    const onSubmit = data => {
        if (data) {
            handleRegister(data)
        }
    }

    /**
        * @function signIn
        * @param
        * @description navigate to login page
    */
    const signIn = () => {
        navigate('/login')
    }

    /**
        * @function onCaptchaChange
        * @params value
        * @description set the captcha value
    */
    const onCaptchaChange = (value) => {
        setRecaptchaResponse(value)
        if (value) {
            setCaptchaPass(true)
        } else {
            setCaptchaPass(false)
        }
    }

    const handleStepOne = async (data) => {
        setStepOneData(data);
        reset({
            company: '',
            companyRegNum: '',
            companyAddress: '',
            designation: '',
            industry: null
        });
        setCurrentStep(2);
    }

    const handleStepTwo = async (data) => {
        setStepTwoData(data);
        reset({
            country: null,
            state: null,
            city: null,
            companySize: null,
            accessCode: ''
        });
        setCurrentStep(3);
    }

    const handleStepThree = async (data) => {
        const finalData = {
            ...stepOneData,
            ...stepTwoData,
            ...data
        };
        handleRegister(finalData);
    }

    const goBack = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
            reset({
                username: stepOneData?.username || '',
                email: stepOneData?.email || '',
                mobile: stepOneData?.mobile || '',
                password: stepOneData?.password || ''
            });
        } else if (currentStep === 3) {
            setCurrentStep(2);
            reset({
                company: stepTwoData?.company || '',
                companyRegNum: stepTwoData?.companyRegNum || '',
                companyAddress: stepTwoData?.companyAddress || '',
                designation: stepTwoData?.designation || '',
                industry: stepTwoData?.industry || null
            });
        }
    }

    const renderStepOne = () => (
        <form className={styles["auth-form"]} onSubmit={handleSubmit(handleStepOne)}>
            <div className={styles["auth-form-content"]}>
                                <div className='mb-4'>
                    <Label className='form-label' for='username'>
                                        Username
                                    </Label>
                                    <Controller
                                        id='username'
                                        name='username'
                                        defaultValue=''
                                        control={control}
                                        render={({ field }) => (
                            <Input {...field} placeholder='Enter your username' className={styles["modern-input"]} invalid={errors.username && true} />
                                        )}
                                        rules={{
                                            required: 'Please enter Username'
                                        }}
                                    />
                                    {errors.username && <FormFeedback>{errors.username.message}</FormFeedback>}
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='email'>
                                        Email
                                    </Label>
                                    <Controller
                                        id='email'
                                        name='email'
                                        defaultValue=''
                                        control={control}
                                        render={({ field }) => (
                            <Input {...field} type='email' placeholder='Enter your business email' className={styles["modern-input"]} invalid={errors.email && true} />
                                        )}
                                        rules={{
                                            required: 'Please enter Email'
                                        }}
                                    />
                                    {errors.email && <FormFeedback>{errors.email.message}</FormFeedback>}
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='mobile'>
                        Phone Number
                    </Label>
                    <Controller
                        id='mobile'
                        name='mobile'
                        defaultValue=''
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                placeholder='Enter phone number'
                                defaultCountry="ZA"
                                className={styles["modern-phone-input"]}
                                value={field?.value}
                                onChange={(e) => {
                                    field.onChange(e)
                                }}
                                {...field}
                            />
                        )}
                    />
                    {errors.mobile && <FormFeedback>{errors.mobile.message}</FormFeedback>}
                </div>

                <div className='mb-4'>
                    <Label className='form-label' for='password'>
                        Password
                    </Label>
                    <Controller
                        id='password'
                        name='password'
                        defaultValue=''
                        control={control}
                        render={({ field }) => (
                            <InputPasswordToggle 
                                {...field} 
                                placeholder='Create a strong password'
                                inputClassName={styles["modern-input"]}
                                invalid={errors.password && true}
                            />
                        )}
                        rules={{
                            required: 'Please enter Password'
                        }}
                    />
                    {errors.password && <FormFeedback>{errors.password.message}</FormFeedback>}
                </div>
            </div>

            <Button 
                className={styles["w-100 modern-button"]} 
                variant='primary'
                type="submit"
            >
                Continue
            </Button>
        </form>
    );

    const renderStepTwo = () => (
        <form className={styles["auth-form"]} onSubmit={handleSubmit(handleStepTwo)}>
            <div className={styles["auth-form-content"]}>
                <div className='mb-4'>
                    <Label className='form-label' for='company'>
                                        Company Name
                                    </Label>
                                    <Controller
                                        id='company'
                                        name='company'
                                        defaultValue=''
                                        control={control}
                                        render={({ field }) => (
                            <Input {...field} placeholder='Enter company name' className={styles["modern-input"]} invalid={errors.company && true} />
                                        )}
                                        rules={{
                                            required: 'Please enter Company name'
                                        }}
                                    />
                                    {errors.company && <FormFeedback>{errors.company.message}</FormFeedback>}
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='companyRegNum'>
                                        Company Registration Number
                                    </Label>
                                    <Controller
                                        id='companyRegNum'
                                        name='companyRegNum'
                                        defaultValue=''
                                        control={control}
                                        render={({ field }) => (
                            <Input {...field} placeholder='Enter registration number' className={styles["modern-input"]} invalid={errors.companyRegNum && true} />
                                        )}
                                        rules={{
                                            required: 'Please enter Company Registration Number'
                                        }}
                                    />
                                    {errors.companyRegNum && <FormFeedback>{errors.companyRegNum.message}</FormFeedback>}
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='companyAddress'>
                                        Company Address
                                    </Label>
                                    <Controller
                                        id='companyAddress'
                                        name='companyAddress'
                                        defaultValue=''
                                        control={control}
                                        render={({ field }) => (
                            <Input {...field} type="textarea" rows="3" placeholder='Enter company address' className={styles["modern-input"]} invalid={errors.companyAddress && true} />
                                        )}
                                        rules={{
                                            required: 'Please enter Company Address'
                                        }}
                                    />
                                    {errors.companyAddress && <FormFeedback>{errors.companyAddress.message}</FormFeedback>}
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='designation'>
                        Job Title
                                    </Label>
                                    <Controller
                                        id='designation'
                                        name='designation'
                                        defaultValue=''
                                        control={control}
                                        render={({ field }) => (
                            <Input {...field} placeholder='Enter your job title' className={styles["modern-input"]} invalid={errors.designation && true} />
                                        )}
                                        rules={{
                            required: 'Job Title is required'
                                        }}
                                    />
                                    {errors.designation && <FormFeedback>{errors.designation.message}</FormFeedback>}
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='industry'>
                                        Industry
                                    </Label>
                                    <Controller
                                        name="industry"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                className={styles["modern-select"]}
                                placeholder="Select your industry"
                                                isClearable
                                                options={industryList}
                                                isSearchable={true}
                                                {...field}
                                            />
                                        )}
                                        rules={{
                                            required: 'Industry is required'
                                        }}
                                    />
                                    {errors?.industry?.message && <span className='text-danger'>{errors?.industry?.message}</span>}
                                </div>
            </div>

            <div className="d-flex gap-3">
                <Button 
                    className={styles["modern-button"]} 
                    variant='secondary'
                    onClick={goBack}
                    style={{ width: '120px' }}
                >
                    Back
                </Button>
                <Button 
                    className={styles["modern-button"]} 
                    variant='primary'
                    type="submit"
                    style={{ flex: 1 }}
                >
                    Continue
                </Button>
            </div>
        </form>
    );

    const renderStepThree = () => (
        <form className={styles["auth-form"]} onSubmit={handleSubmit(handleStepThree)}>
            <div className={styles["auth-form-content"]}>
                                <div className='mb-4'>
                    <Label className='form-label' for='country'>
                                        Country
                                    </Label>
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                className={styles["modern-select"]}
                                placeholder="Select your country"
                                                isClearable
                                                options={countryList}
                                                isSearchable={true}
                                                {...field}
                                            />
                                        )}
                                        rules={{
                                            required: 'Country is required'
                                        }}
                                    />
                                    {errors?.country?.message && <span className='text-danger'>{errors?.country?.message}</span>}
                                </div>

                                <div className='d-flex flex-row'>
                                    <div className='mb-4 col-6 pe-2'>
                        <Label className='form-label' for='state'>
                            State/Province
                                        </Label>
                                        <Controller
                                            name="state"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                    className={styles["modern-select"]}
                                    placeholder="Select state"
                                                    isClearable
                                                    options={stateList}
                                                    isSearchable={true}
                                                    {...field}
                                                />
                                            )}
                                            rules={{
                                                required: 'State is required'
                                            }}
                                        />
                                        {errors?.state?.message && <span className='text-danger'>{errors?.state?.message}</span>}
                                    </div>

                                    <div className='mb-4 col-6 ps-2'>
                        <Label className='form-label' for='city'>
                                            City
                                        </Label>
                                        <Controller
                                            name="city"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                    className={styles["modern-select"]}
                                    placeholder="Select city"
                                                    isClearable
                                                    options={cityList}
                                                    isSearchable={true}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='companySize'>
                        Annual Revenue
                                    </Label>
                                    <Controller
                                        name="companySize"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                className={styles["modern-select"]}
                                placeholder="Select annual revenue"
                                                isClearable
                                                options={companySizeList}
                                                isSearchable={true}
                                                {...field}
                                            />
                                        )}
                                        rules={{
                            required: 'Annual Revenue is required'
                                        }}
                                    />
                                    {errors?.companySize?.message && <span className='text-danger'>{errors?.companySize?.message}</span>}
                                </div>

                                <div className='mb-4'>
                    <Label className='form-label' for='accessCode'>
                        Access Code
                        <span id="UncontrolledTooltipExample" className={`${styles["ms-2"]} ${styles["text-primary"]}`}>
                            <BsQuestionCircle size={12} />
                        </span>
                                    </Label>
                                    <Controller
                                        id='accessCode'
                                        name='accessCode'
                                        defaultValue=''
                                        control={control}
                                        render={({ field }) => (
                            <Input {...field} placeholder='Enter access code (optional)' className={styles["modern-input"]} />
                        )}
                    />
                    <UncontrolledTooltip placement="top" target="UncontrolledTooltipExample">
                        Access code is not required for registration. Only for companies that are participating in the supply chain program.
                    </UncontrolledTooltip>
                                </div>

                                <div className='mb-4'>
                                    <FormGroup check>
                        <Input type="checkbox" required className={styles["modern-checkbox"]} />
                        <Label check className={styles["modern-checkbox-label"]}>
                            I agree to the{' '}
                            <a href={'/terms&condition'} target='_blank' className={`${styles["text-primary"]} ${styles["cursor-pointer"]}`}>
                                Terms & Conditions
                                            </a>
                                        </Label>
                                    </FormGroup>
                                </div>
                            </div>
            
            <div className='mb-4'>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                    className={styles["google-recaptcha"]}
                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                onChange={onCaptchaChange}
                            />
                        </div>

            <div className="d-flex gap-3">
                <Button 
                    className={styles["modern-button"]} 
                    variant='secondary'
                    onClick={goBack}
                    style={{ width: '120px' }}
                >
                    Back
                </Button>
                <Button 
                    className={styles["modern-button"]} 
                    variant='primary'
                    disabled={!captchaPass} 
                    type="submit"
                    style={{ flex: 1 }}
                >
                    {isLoading ? <Spinner size='sm' /> : 'Create Account'}
                            </Button>
                        </div>
                    </form>
    );

    return <Container fluid className={styles["auth-container"]}>
        <div>
            <Toaster />
        </div>

        <Row className={`min-vh-100 ${styles["auth-row"]}`}>
            <Col lg="6" className="d-none d-lg-flex align-items-center p-0">
                <div className={`${styles["auth-cover-bg"]} w-100 h-100 d-flex align-items-center justify-content-center`}>
                    <div className={`${styles["auth-cover-content"]} text-center`}>
                        <h1 className="text-white mb-4">Welcome to the SME and Supplier ESG Readiness Tool</h1>
                        <p className="text-white-50">Join our community of businesses committed to sustainable growth</p>
                    </div>
                </div>
            </Col>
            <Col lg="6" className={`${styles["auth-content"]}`}>
                <div className={styles["auth-form-wrapper"]}>
                    <div className={styles["auth-header"]}>
                        <h2 className={styles["auth-title"]}>Create your account</h2>
                        <p className={styles["auth-subtitle"]}>
                            Step {currentStep} of 3: {currentStep === 1 ? 'Basic Information' : currentStep === 2 ? 'Company Information' : 'Additional Details'}
                        </p>
                        <p className={styles["auth-subtitle"]}>
                            Already have an account? <span className={`${styles["text-primary"]} ${styles["cursor-pointer"]}`} onClick={signIn}>Sign In</span>
                        </p>
                    </div>
                    {currentStep === 1 ? renderStepOne() : currentStep === 2 ? renderStepTwo() : renderStepThree()}
            </div>
            </Col>
        </Row>
    </Container>
};

export default RegisterPage;